import type {
  AppApi,
  AppRole,
  AdminUgcApi,
  AdminImageGenApi,
  AdminVideoGenApi,
  AdminSpriteGenApi,
  AdminAudioGenApi,
  AdminThreeDGenApi,
  ImageGenEntry,
  ImageGenReport,
  VideoGenEntry,
  VideoGenReport,
  SpriteGenEntry,
  SpriteGenReport,
  AudioGenEntry,
  AudioGenReport,
  ThreeDGenEntry,
  ThreeDGenReport,
  ReleaseNote,
} from './AppApi'
import type { LaunchIntent, ResolveLaunchIntentOptions } from './LaunchIntent'
import type { RundotGameAPI } from '../rundot-game-api/types'
import { MockAdminUgcApi } from './MockAdminUgcApi'
import { createMockAdminGenApi } from './adminGen'
import { mockLog } from '../MockDebug'

/** Shared sample changelog used by the mock + playground app hosts. */
export const MOCK_RELEASE_NOTES: ReleaseNote[] = [
  {
    versionNumber: '1.2.0',
    changelog:
      '# Major Update\n- New ranked ladder\n- Smarter matchmaking\n- Stability fixes',
    releaseType: 'major',
    publishedAt: new Date().toISOString(),
  },
  {
    versionNumber: '1.1.0',
    changelog: '## Update\n- Balance tweaks\n- Bug fixes',
    releaseType: 'minor',
    publishedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

export class MockAppApi implements AppApi {
  readonly adminUgc: AdminUgcApi
  readonly adminImageGen: AdminImageGenApi
  readonly adminVideoGen: AdminVideoGenApi
  readonly adminSpriteGen: AdminSpriteGenApi
  readonly adminAudioGen: AdminAudioGenApi
  readonly adminThreeDGen: AdminThreeDGenApi

  constructor(private readonly rundotGameApi: RundotGameAPI) {
    this.adminUgc = new MockAdminUgcApi()
    this.adminImageGen = createMockAdminGenApi<ImageGenEntry, ImageGenReport>('adminImageGen')
    this.adminVideoGen = createMockAdminGenApi<VideoGenEntry, VideoGenReport>('adminVideoGen')
    this.adminSpriteGen = createMockAdminGenApi<SpriteGenEntry, SpriteGenReport>('adminSpriteGen')
    this.adminAudioGen = createMockAdminGenApi<AudioGenEntry, AudioGenReport>('adminAudioGen')
    this.adminThreeDGen = createMockAdminGenApi<ThreeDGenEntry, ThreeDGenReport>('adminThreeDGen')
  }

  async getMyRole(): Promise<AppRole> {
    const mockRole = (
      this.rundotGameApi._mock as Record<string, Record<string, unknown>>
    )?.app?.role as string | undefined
    if (mockRole === 'owner' || mockRole === 'editor' || mockRole === 'none') {
      console.warn(
        `[RUN:mock] app.getMyRole() returning mock role: '${mockRole}'`,
      )
      return mockRole
    }
    console.warn(
      "[RUN:mock] app.getMyRole() returning 'none'. Set _mock.app.role to 'owner', 'editor', or 'none' to control.",
    )
    return 'none'
  }

  async resolveLaunchIntent(
    _options?: ResolveLaunchIntentOptions,
  ): Promise<LaunchIntent> {
    const mockIntent = (
      this.rundotGameApi._mock as Record<string, Record<string, unknown>>
    )?.app?.launchIntent as LaunchIntent | undefined
    if (mockIntent) {
      console.warn(
        `[RUN:mock] app.resolveLaunchIntent() returning mock intent: '${mockIntent.kind}'`,
      )
      return mockIntent
    }
    return { kind: 'none', params: {} }
  }

  async getReleaseNotesAsync(): Promise<ReleaseNote[]> {
    mockLog('Mock App', 'getReleaseNotesAsync')
    return MOCK_RELEASE_NOTES
  }

  async openReleaseNotesAsync(): Promise<void> {
    mockLog('Mock App', 'openReleaseNotesAsync (no-op in mock host)')
  }
}
